/* eslint-disable */
import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'

import Table from 'components/Table'
import ItemsPerPageDropdown from 'components/ItemsPerPageDropdown'
import Pagination from 'features/Pagination'
import sortDirection from 'constants/sortDirection'
import { usePagination } from 'hooks/usePagination'
import { showErrorToast, showSuccessToast } from 'utils/toast'
import LoadingWrapper from 'components/LoadingWrapper'
import { CountriesContext } from 'providers/countries'

import {
  requestGetSensitiveSites,
  requestApproveSensitiveSite,
  requestRejectSensitiveSite
} from 'api/sensitiveSpecies'
import { makeSensitiveSitesColumns, makeSensitiveSitesRows } from './sensitiveSpeciesTable'
import {
  SensitiveSpeciesPageContainer,
  SensitiveSpeciesPageTitle,
  SensitiveSpeciesTableWrapper,
  SensitiveSpeciesFooterWrapper
} from './style'

const defaultPaginationState = {
  totalNumber: 0,
  resultsPerPage: 12,
  totalPages: 0,
  currentPage: 1,
  hasNext: true,
  hasPrevious: false
}

// Reassessment proposal purposes that require sensitive species handling
const REASSESSMENT_PURPOSE_VALUES = [3, 4, 5, 6]

let sitesFetchAbortController = null

const SensitiveSpeciesManagement = () => {
  const { countriesOptions } = useContext(CountriesContext) || {}
  const { pagination, setPagination, selectPage, nextPage, prevPage, nextSection, prevSection, changeResultsPerPage } =
    usePagination(defaultPaginationState)

  const [sorting, setSorting] = useState({
    sortBy: 'siteCode',
    sortDirection: sortDirection.ASC
  })

  const [sites, setSites] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [actionInProgressId, setActionInProgressId] = useState(null)

  const getSensitiveSites = useCallback(() => {
    if (sitesFetchAbortController) {
      setIsLoading(false)
      sitesFetchAbortController.abort()
    }

    sitesFetchAbortController = new AbortController()

    const params = {
      resultsPerPage: pagination.resultsPerPage,
      currentPage: pagination.currentPage,
      sortBy: sorting.sortBy,
      sortDirection: sorting.sortDirection,
      proposalPurpose: REASSESSMENT_PURPOSE_VALUES,
      signal: sitesFetchAbortController.signal
    }

    setIsLoading(true)

    requestGetSensitiveSites(params)
      .then((data) => {
        setIsLoading(false)
        const sitesData =
          data?.sites || data?.sensitiveSites || (Array.isArray(data) ? data : data?.data) || []

        const filteredSites = (sitesData || []).filter((item) => {
          const siteHistory = item.SitesHistory || item
          const proposalPurpose = siteHistory?.proposalPurpose ?? item.proposalPurpose
          if (proposalPurpose === null || proposalPurpose === undefined) {
            return true
          }
          return REASSESSMENT_PURPOSE_VALUES.includes(Number(proposalPurpose))
        })

        setSites(filteredSites)
        const paginationData = data?.pagination || defaultPaginationState
        setPagination(paginationData)
      })
      .catch((err) => {
        setIsLoading(false)
        if (err.name === 'AbortError') {
          return
        }
        showErrorToast(
          <FormattedMessage
            id="failedToLoadSensitiveSites"
            defaultMessage="Unable to load sensitive sites. Please try again."
          />,
          'showBorder'
        )
      })
  }, [pagination.resultsPerPage, pagination.currentPage, sorting.sortBy, sorting.sortDirection])

  useEffect(() => {
    getSensitiveSites()
  }, [getSensitiveSites])

  useEffect(() => {
    const handleRefreshEvent = () => {
      getSensitiveSites()
    }
    window.addEventListener('sensitiveSpeciesDataChanged', handleRefreshEvent)

    return () => {
      window.removeEventListener('sensitiveSpeciesDataChanged', handleRefreshEvent)
    }
  }, [getSensitiveSites])

  const isAlreadyProcessedResponse = (result, forApprove) => {
    if (!result) return false
    const alreadyFlag =
      result.alreadyProcessed === true ||
      (forApprove ? result.alreadyApproved === true : result.alreadyRejected === true)
    if (alreadyFlag) return true
    const msg = typeof result.message === 'string' ? result.message : ''
    const err = typeof result.error === 'string' ? result.error : ''
    const text = `${msg} ${err}`.toLowerCase()
    return text.length > 0 && text.includes('already')
  }

  const handleApprove = async (siteItem) => {
    const siteHistoryId = siteItem.historyId ?? siteItem.SitesHistory?.id ?? siteItem.id
    if (siteHistoryId == null || siteHistoryId === '') {
      showErrorToast(
        <FormattedMessage id="failedToApproveSensitiveSite" defaultMessage="Unable to approve this site. Please try again." />,
        'showBorder'
      )
      return
    }
    if (actionInProgressId != null) return
    setActionInProgressId(siteHistoryId)
    try {
      const result = await requestApproveSensitiveSite(siteHistoryId)
      if (isAlreadyProcessedResponse(result, true)) {
        showSuccessToast(
          <FormattedMessage id="sensitiveSpeciesAlreadyApproved" defaultMessage="This site has already been approved." />
        )
        getSensitiveSites()
        return
      }
      const isError =
        result?.error != null ||
        result?.success === false ||
        (typeof result?.message === 'string' && result.message.toLowerCase().includes('error'))
      if (isError) {
        const serverMessage = result?.error ?? result?.message
        const message = typeof serverMessage === 'string' && serverMessage
          ? serverMessage
          : null
        showErrorToast(
          message ?? (
            <FormattedMessage id="failedToApproveSensitiveSite" defaultMessage="Unable to approve this site. Please try again." />
          ),
          'showBorder'
        )
        return
      }
      showSuccessToast(
        <FormattedMessage id="sensitiveSiteApproved" defaultMessage="Sensitive species site has been approved successfully." />
      )
      getSensitiveSites()
    } catch (error) {
      if (error?.name === 'AbortError') return
      const message = error?.message ?? error?.response?.data?.message ?? error?.response?.data?.error
      const messageStr = typeof message === 'string' ? message.toLowerCase() : ''
      if (messageStr.includes('already')) {
        showSuccessToast(
          <FormattedMessage id="sensitiveSpeciesAlreadyApproved" defaultMessage="This site has already been approved." />
        )
        getSensitiveSites()
        return
      }
      showErrorToast(
        typeof message === 'string' && message
          ? message
          : (
              <FormattedMessage id="failedToApproveSensitiveSite" defaultMessage="Unable to approve this site. Please try again." />
            ),
        'showBorder'
      )
    } finally {
      setActionInProgressId(null)
    }
  }

  const handleReject = async (siteItem) => {
    const siteHistoryId = siteItem.historyId ?? siteItem.SitesHistory?.id ?? siteItem.id
    if (siteHistoryId == null || siteHistoryId === '') {
      showErrorToast(
        <FormattedMessage id="failedToRejectSensitiveSite" defaultMessage="Unable to reject this site. Please try again." />,
        'showBorder'
      )
      return
    }
    if (actionInProgressId != null) return
    setActionInProgressId(siteHistoryId)
    try {
      const result = await requestRejectSensitiveSite(siteHistoryId)
      if (isAlreadyProcessedResponse(result, false)) {
        showSuccessToast(
          <FormattedMessage id="sensitiveSpeciesAlreadyRejected" defaultMessage="This site has already been rejected." />
        )
        getSensitiveSites()
        return
      }
      const isError =
        result?.error != null ||
        result?.success === false ||
        (typeof result?.message === 'string' && result.message.toLowerCase().includes('error'))
      if (isError) {
        const serverMessage = result?.error ?? result?.message
        const message = typeof serverMessage === 'string' && serverMessage
          ? serverMessage
          : null
        showErrorToast(
          message ?? (
            <FormattedMessage id="failedToRejectSensitiveSite" defaultMessage="Unable to reject this site. Please try again." />
          ),
          'showBorder'
        )
        return
      }
      showSuccessToast(
        <FormattedMessage id="sensitiveSiteRejected" defaultMessage="Sensitive species site has been rejected." />
      )
      getSensitiveSites()
    } catch (error) {
      if (error?.name === 'AbortError') return
      const message = error?.message ?? error?.response?.data?.message ?? error?.response?.data?.error
      const messageStr = typeof message === 'string' ? message.toLowerCase() : ''
      if (messageStr.includes('already')) {
        showSuccessToast(
          <FormattedMessage id="sensitiveSpeciesAlreadyRejected" defaultMessage="This site has already been rejected." />
        )
        getSensitiveSites()
        return
      }
      showErrorToast(
        typeof message === 'string' && message
          ? message
          : (
              <FormattedMessage id="failedToRejectSensitiveSite" defaultMessage="Unable to reject this site. Please try again." />
            ),
        'showBorder'
      )
    } finally {
      setActionInProgressId(null)
    }
  }

  const columns = useMemo(() => makeSensitiveSitesColumns(sorting, setSorting), [sorting])
  const rows = useMemo(
    () => makeSensitiveSitesRows(sites, handleApprove, handleReject, countriesOptions, actionInProgressId),
    [sites, handleApprove, handleReject, countriesOptions, actionInProgressId]
  )

  return (
    <>
      <SensitiveSpeciesPageContainer>
        <SensitiveSpeciesPageTitle data-test="legend">
          <FormattedMessage id="sensitiveSpecies" defaultMessage="Sensitive site approval" />
        </SensitiveSpeciesPageTitle>
        <SensitiveSpeciesTableWrapper>
          <LoadingWrapper size={'24px'} isLoading={isLoading}>
            <Table
              rows={rows}
              columns={columns}
              noResultMessage="noSensitiveSitesFound"
              dataTest="sensitive-sites-list"
              clearFilters={() => {}}
              isLoading={isLoading}
            />
          </LoadingWrapper>
          <SensitiveSpeciesFooterWrapper data-test="pagination">
            <ItemsPerPageDropdown
              currentPage={pagination.currentPage}
              resultsPerPage={pagination.resultsPerPage}
              totalNumber={pagination.totalNumber}
              onChange={changeResultsPerPage}
              dataTest="sensitive-sites-items-per-page"
              menuPlacement="top"
            />
            {pagination.totalPages > 1 && (
              <Pagination
                activePages={pagination.currentPage}
                numberOfPages={pagination.totalPages}
                nextPage={nextPage}
                prevPage={prevPage}
                nextSection={nextSection}
                prevSection={prevSection}
                selectPage={selectPage}
                dataTest="page-number"
              />
            )}
          </SensitiveSpeciesFooterWrapper>
        </SensitiveSpeciesTableWrapper>
      </SensitiveSpeciesPageContainer>
    </>
  )
}

export default SensitiveSpeciesManagement

